import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Shield, BarChart3, Settings2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: true, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem('pch_cookie_consent');
    if (!saved) {
      setTimeout(() => setVisible(true), 1500);
    }
  }, []);

  const accept = (all) => {
    const consent = all ? { necessary: true, analytics: true, marketing: true } : prefs;
    localStorage.setItem('pch_cookie_consent', JSON.stringify(consent));
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem('pch_cookie_consent', JSON.stringify({ necessary: true, analytics: false, marketing: false }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card/95 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl shadow-primary/10 overflow-hidden">
            {/* Gradient top border */}
            <div className="h-1 bg-gradient-to-r from-primary via-accent to-primary" />

            {!showPrefs ? (
              <div className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-foreground text-base mb-1">We use cookies 🍪</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      We use cookies and similar technologies to provide the best experience, analyse our traffic, and personalise content. By clicking "Accept All", you consent to our use of cookies.{' '}
                      <a href="/cookie-policy" className="text-primary hover:underline">Cookie Policy</a> ·{' '}
                      <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>
                    </p>
                  </div>
                  <button onClick={decline} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 mt-5">
                  <Button onClick={() => accept(true)} className="bg-gradient-to-r from-primary to-primary/80 shadow-md shadow-primary/20 text-sm">
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Accept All
                  </Button>
                  <Button variant="outline" onClick={decline} className="text-sm">Reject Non-Essential</Button>
                  <Button variant="ghost" onClick={() => setShowPrefs(true)} className="text-sm gap-1.5 text-muted-foreground">
                    <Settings2 className="w-3.5 h-3.5" /> Preferences
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-foreground text-base">Cookie Preferences</h3>
                  <button onClick={() => setShowPrefs(false)} className="p-1.5 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3 mb-5">
                  {[
                    { key: 'necessary', icon: Shield, label: 'Necessary', desc: 'Essential for the website to function. Cannot be disabled.', locked: true },
                    { key: 'analytics', icon: BarChart3, label: 'Analytics', desc: 'Help us understand how visitors interact with the website (Google Analytics, Hotjar).', locked: false },
                    { key: 'marketing', icon: Cookie, label: 'Marketing', desc: 'Used to deliver personalised advertisements and track ad campaigns.', locked: false },
                  ].map(({ key, icon: Icon, label, desc, locked }) => (
                    <div key={key} className="flex items-start gap-3 bg-muted/20 border border-border/30 rounded-xl p-3">
                      <Icon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                      <Switch
                        checked={!!prefs[key]}
                        disabled={locked}
                        onCheckedChange={(v) => !locked && setPrefs(p => ({ ...p, [key]: v }))}
                        className="mt-0.5"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Button onClick={() => accept(false)} className="bg-gradient-to-r from-primary to-primary/80 text-sm">Save Preferences</Button>
                  <Button variant="outline" onClick={() => accept(true)} className="text-sm">Accept All</Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}